import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

export default function SearchBar({
  searchTerm,
  setSearchTerm,
  selectedCompanyNames = [],
  onAddSelectedCompanyName,
  onRemoveSelectedCompanyName,
  onClearSelectedCompanyNames,
  savedSearchName,
  setSavedSearchName,
  saveCurrentSearch,
  savedSearches,
  selectedSavedSearch,
  loadSavedSearch,
  deleteSavedSearch,
  savedSearchesBusy = false,
  searchLabel = "Search companies or investors...",
  searchPlaceholder,
  saveNameLabel = "Save search as...",
  loadLabel = "Load Search",
  saveButtonLabel = "Save Search",
  deleteButtonLabel = "Delete",
}) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const trimmedSearchTerm = searchTerm.trim();
  const showSearchChip = trimmedSearchTerm !== "" && !isSearchFocused;
  const handleSearchKeyDown = (event) => {
    if (event.key !== "Enter" || !trimmedSearchTerm) return;

    const added = onAddSelectedCompanyName?.(trimmedSearchTerm);
    if (added) {
      event.preventDefault();
    }
  };

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <TextField
        label={searchLabel}
        variant="outlined"
        size="small"
        fullWidth
        value={showSearchChip ? "" : searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleSearchKeyDown}
        onFocus={() => setIsSearchFocused(true)}
        onBlur={() => setIsSearchFocused(false)}
        placeholder={showSearchChip ? "" : searchPlaceholder}
        InputProps={{
          startAdornment: showSearchChip ? (
            <InputAdornment position="start">
              <Chip
                label={trimmedSearchTerm}
                onDelete={() => setSearchTerm("")}
                onMouseDown={(event) => event.preventDefault()}
              />
            </InputAdornment>
          ) : undefined,
        }}
      />

      {selectedCompanyNames.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            maxHeight: 118,
            overflowY: "auto",
          }}
        >
          {selectedCompanyNames.map((companyName) => (
            <Chip
              key={companyName}
              label={`Company: ${companyName}`}
              size="small"
              onDelete={() => onRemoveSelectedCompanyName?.(companyName)}
              sx={{
                maxWidth: "100%",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          ))}
          {selectedCompanyNames.length > 1 && (
            <Chip
              label="Clear companies"
              size="small"
              variant="outlined"
              onDelete={onClearSelectedCompanyNames}
              onClick={onClearSelectedCompanyNames}
            />
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 140px" },
          gap: 1.5,
          width: "100%",
        }}
      >
        <TextField
          label={saveNameLabel}
          variant="outlined"
          size="small"
          fullWidth
          value={savedSearchName}
          onChange={(e) => setSavedSearchName(e.target.value)}
        />

        <Button
          variant="outlined"
          onClick={saveCurrentSearch}
          disabled={!savedSearchName.trim() || savedSearchesBusy}
          sx={{ height: 34, whiteSpace: "nowrap", fontSize: "0.82rem" }}
        >
          {saveButtonLabel}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 140px" },
          gap: 1.5,
          width: "100%",
        }}
      >
        <FormControl fullWidth size="small">
          <InputLabel>{loadLabel}</InputLabel>
          <Select
            value={selectedSavedSearch}
            label={loadLabel}
            onChange={(e) => loadSavedSearch(e.target.value)}
            disabled={savedSearchesBusy}
          >
            <MenuItem value="">None</MenuItem>
            {savedSearches.map((search) => (
              <MenuItem key={search.name} value={search.name}>
                {search.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          color="error"
          onClick={deleteSavedSearch}
          disabled={!selectedSavedSearch || savedSearchesBusy}
          sx={{ height: 34, whiteSpace: "nowrap", fontSize: "0.82rem" }}
        >
          {deleteButtonLabel}
        </Button>
      </Box>
    </Box>
  );
}
